// src/pages/insights/insights-detail.tsx
//
// Page détail d'un insight (post LinkedIn republié).
// Rendu format "post" plutôt qu'article long : typo aérée, paragraphes
// courts, préservation des sauts de ligne du post original.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Linkedin,
  Calendar,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Sparkles,
  Download,
  Calculator,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { supabase } from '../../lib/supabase';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import { sanitizeHtml } from '../../lib/sanitizeHtml';

interface Insight {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  author: string | null;
  image_url: string | null;
  published_date: string | null;
  created_at: string;
  updated_at: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Strip HTML if présent et préserve les sauts de ligne du post LinkedIn
 * original. Si le content est déjà du HTML enrichi (cas d'un article), on
 * le rend tel quel.
 */
function isHtml(s: string): boolean {
  return /<\/?(p|h[1-6]|ul|ol|li|strong|em|br|div|blockquote|table)\b/i.test(s);
}

export default function InsightsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [insight, setInsight] = useState<Insight | null>(null);
  const [siblings, setSiblings] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let mounted = true;

    async function load() {
      setLoading(true);
      setNotFound(false);

      try {
        const { data, error } = await supabase
          .from('articles')
          .select('*')
          .eq('slug', slug)
          .eq('category', 'insights')
          .eq('published', true)
          .maybeSingle();

        if (!mounted) return;

        if (error) {
          console.error('[insight detail] error:', error.message);
          setNotFound(true);
          return;
        }
        if (!data) {
          setNotFound(true);
          return;
        }

        setInsight(data as Insight);

        // Charge 3 autres insights pour la sidebar
        const { data: sib } = await supabase
          .from('articles')
          .select('id, slug, title, excerpt, content, published_date, created_at')
          .eq('published', true)
          .eq('category', 'insights')
          .neq('slug', slug)
          .order('created_at', { ascending: false })
          .limit(3);

        if (mounted) setSiblings((sib as Insight[]) ?? []);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="container-custom max-w-4xl py-24">
          <div className="animate-pulse space-y-4">
            <div className="h-3 bg-neutral-100 rounded w-1/3" />
            <div className="h-10 bg-neutral-100 rounded w-3/4" />
            <div className="h-3 bg-neutral-100 rounded w-full" />
            <div className="h-3 bg-neutral-100 rounded w-5/6" />
            <div className="h-3 bg-neutral-100 rounded w-4/6" />
          </div>
        </div>
      </Layout>
    );
  }

  if (notFound || !insight) {
    return (
      <Layout title="Insight introuvable | recruteurs.io">
        {/* ⚠️ SEO : noindex pour éviter Soft 404 GSC sur slugs zombies */}
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="container-custom max-w-3xl py-24 text-center">
          <p className="text-5xl font-bold text-neutral-200 mb-4">404</p>
          <h1 className="text-2xl font-bold text-[#30475e] mb-3">
            Insight introuvable
          </h1>
          <p className="text-neutral-500 mb-6">
            Ce post a peut-être été dépublié ou son URL a changé.
          </p>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold text-white whitespace-nowrap"
          >
            Voir tous les insights
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Layout>
    );
  }

  const author = insight.author ?? 'Gérald Gaillard';
  const date = formatDate(insight.published_date ?? insight.created_at);
  const url = `https://recruteurs.io/insights/${insight.slug}`;
  const description =
    insight.excerpt ??
    insight.content.replace(/<[^>]+>/g, ' ').slice(0, 160).trim() + '…';

  return (
    <Layout
      title={`${insight.title} · Insight LinkedIn | recruteurs.io`}
      description={description}
    >
      <WebPageSchema
        page={{
          title: `${insight.title} · Insight LinkedIn | recruteurs.io`,
          description,
          url,
          lastReviewed: (insight.updated_at ?? insight.created_at).slice(0, 10),
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Insights', url: 'https://recruteurs.io/insights' },
          { name: insight.title, url },
        ]}
      />

      {/* HERO bandeau compact */}
      <SectionBackground
        page="insight"
        section="hero"
        className="bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <Section>
          <div className="max-w-3xl mx-auto py-8">
            <button
              onClick={() => navigate('/insights')}
              className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#d03484] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Tous les insights
            </button>

            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-4">
              <Linkedin className="w-3.5 h-3.5 text-[#0077b5]" />
              <span>Insight LinkedIn</span>
              <span className="text-neutral-300">·</span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {date}
              </span>
              <span className="text-neutral-300">·</span>
              <span>par {author}</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#30475e] leading-[1.1]">
              {insight.title}
            </h1>
          </div>
        </Section>
      </SectionBackground>

      {/* CONTENU + SIDEBAR */}
      <SectionBackground page="insight" section="body" className="bg-white">
        <Section>
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            {/* Body */}
            <motion.article
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm p-7 md:p-10"
            >
              {/* Auteur header style LinkedIn */}
              <div className="flex items-center gap-3 pb-5 mb-6 border-b border-neutral-100">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold text-base shadow-md shadow-[#d03484]/20">
                  {author
                    .split(/\s+/)
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-bold text-[#30475e]">{author}</p>
                  <p className="text-xs text-neutral-500">
                    Fondateur recruteurs.io · loocatme.fr · resoo.fr
                  </p>
                </div>
              </div>

              {/* Post content — préserve les sauts de ligne du post original */}
              {isHtml(insight.content) ? (
                <div
                  className="prose prose-lg max-w-none
                    prose-p:text-[#30475e]/90 prose-p:leading-[1.85] prose-p:mb-4
                    prose-strong:text-[#30475e] prose-strong:font-semibold
                    prose-a:text-[#d03484] prose-a:no-underline prose-a:font-semibold hover:prose-a:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(insight.content) }}
                />
              ) : (
                <div
                  className="text-base md:text-lg text-[#30475e]/90 leading-[1.85] whitespace-pre-wrap"
                  style={{ wordWrap: 'break-word' }}
                >
                  {insight.content}
                </div>
              )}

              {/* CTA cluster — chaque insight = porte d'entrée vers les assets */}
              <div className="mt-10 pt-6 border-t border-neutral-100">
                <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-4">
                  Pour aller plus loin
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link
                    to="/decouvrir"
                    className="group flex items-center gap-3 rounded-xl bg-[#fbfaf8] hover:bg-white hover:ring-1 hover:ring-[#d03484]/30 hover:shadow-md p-4 transition-all"
                  >
                    <Sparkles className="w-5 h-5 text-[#d03484] flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-[#30475e]">
                        Plateforme Parcours Découverte
                      </p>
                      <p className="text-xs text-neutral-500">
                        Notre méthode propriétaire
                      </p>
                    </div>
                  </Link>
                  <Link
                    to="/ressources/calculateur-roi"
                    className="group flex items-center gap-3 rounded-xl bg-[#fbfaf8] hover:bg-white hover:ring-1 hover:ring-[#d03484]/30 hover:shadow-md p-4 transition-all"
                  >
                    <Calculator className="w-5 h-5 text-[#d03484] flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-[#30475e]">
                        Calculateur ROI
                      </p>
                      <p className="text-xs text-neutral-500">
                        Estimez votre coût turnover
                      </p>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.article>

            {/* Sidebar */}
            <aside className="space-y-5">
              {/* Livre blanc CTA */}
              <div className="rounded-2xl bg-gradient-to-br from-[#30475e] to-[#1f3245] p-6 text-white shadow-lg">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#fde7f2] mb-3">
                  <Download className="w-3 h-3" />
                  Livre blanc gratuit
                </span>
                <p className="text-base font-bold leading-tight mb-2">
                  12 chiffres qui changent le recrutement en 2026
                </p>
                <p className="text-xs text-white/75 leading-relaxed mb-4">
                  PDF de 28 pages · Sources INSEE, APEC, DARES
                </p>
                <Link
                  to="/entreprise/recrutement"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-bold text-[#30475e] hover:bg-[#fde7f2] transition-all w-full whitespace-nowrap"
                >
                  Le télécharger
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              {/* Autres insights */}
              {siblings.length > 0 && (
                <div className="rounded-2xl bg-white ring-1 ring-neutral-200 p-6">
                  <p className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-4">
                    Autres insights
                  </p>
                  <div className="space-y-4">
                    {siblings.map((s) => (
                      <Link
                        key={s.id}
                        to={`/insights/${s.slug}`}
                        className="block group"
                      >
                        <p className="text-sm font-semibold text-[#30475e] group-hover:text-[#d03484] transition-colors leading-snug mb-1">
                          {s.title}
                        </p>
                        <p className="text-[11px] text-neutral-400">
                          {formatDate(s.published_date ?? s.created_at)}
                        </p>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/insights"
                    className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-[#d03484] hover:underline"
                  >
                    Tous les insights
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </aside>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
